/**
 * PlaybookCard — the monthly short-strangle playbook, live, for whatever is loaded:
 *
 *   ENTRY (a new short call + short put being built)
 *     · premium balance (dearer ÷ cheaper) with a one-click "Match premiums"
 *     · monthly-expiry hint (enter in the first days of the month)
 *   RUNNING (a held basket with one short call + one short put)
 *     · premium ratio vs the alert threshold (the "3× rule") → "Suggest roll"
 *     · straddle status (the point where the playbook stops adjusting)
 *     · adjustments so far and what they booked
 *     · total P&L (booked + open) against the FIXED ₹ target / stop, if set
 *
 * Pure presentation: every action is a callback into LegsPanel.
 */
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { WsLeg, shortPair, isMonthlyExpiry, isNewLeg } from './wsMath';
import { WsColors, Ccy, fmtMoney, fmtPx, fmtStrike, expLabel } from './wsUi';
import { USD_TO_INR } from '../../../utils/constants';
import type { HeldMargin } from './LegsPanel';

interface Props {
  legs: WsLeg[];
  held: Record<number, any>;
  /** Margin each held basket holds (Delta's figure for LIVE, the app's estimate otherwise). */
  margin?: Record<number, HeldMargin>;
  ratioX: number;
  ccy: Ccy;
  c: WsColors;
  onMatch: () => void;
  onSuggestRoll: (basketId: number) => void;
  onExitRules: (basketId: number) => void;
  onCloseBasket: (basketId: number) => void;
}

const priceOf = (l: WsLeg) => (l.mark != null && l.mark > 0 ? l.mark : l.price);

const PlaybookCard: React.FC<Props> = ({ legs, held, margin, ratioX, ccy, c, onMatch, onSuggestRoll, onExitRules, onCloseBasket }) => {
  const inr = (v: number, sign = false) => fmtMoney(v / USD_TO_INR, ccy, { sign });

  const ratioBar = (ratio: number) => {
    const col = ratio >= ratioX ? c.red : ratio >= ratioX * 0.83 ? c.amber : c.green;
    return (
      <Tooltip title={`Premium ratio: the dearer short ÷ the cheaper short. Playbook: adjust when it reaches ${ratioX}×.`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
          <Box sx={{ position: 'relative', flex: 1, height: 6, borderRadius: 3, bgcolor: c.hover, minWidth: 50 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, ((ratio - 1) / Math.max(0.01, ratioX - 1)) * 100)}%`, bgcolor: col, borderRadius: 3 }} />
          </Box>
          <Box component="b" sx={{ color: col, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{ratio.toFixed(2)}× <span style={{ color: c.faint, fontWeight: 400 }}>/ {ratioX}×</span></Box>
        </Box>
      </Tooltip>
    );
  };

  const btn = (label: string, onClick: () => void, tone: 'amber' | 'plain' | 'red' = 'plain', tip?: string) => (
    <Tooltip title={tip || ''} disableInteractive>
      <Box component="button" type="button" onClick={onClick}
        sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 0.8, whiteSpace: 'nowrap',
          border: `1px solid ${tone === 'amber' ? c.amber : tone === 'red' ? c.red : c.border}`,
          color: tone === 'amber' ? c.amber : tone === 'red' ? c.red : c.dim, '&:hover': { bgcolor: c.hover } }}>{label}</Box>
    </Tooltip>
  );

  const blocks: React.ReactNode[] = [];

  // ── entry: the new basket's short pair ──
  const fresh = shortPair(legs.filter(isNewLeg));
  if (fresh) {
    const { ce, pe, ratio } = fresh;
    const monthly = isMonthlyExpiry(ce.expiry);
    blocks.push(
      <Box key="entry" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ fontWeight: 700 }}>Entry</Box>
          <Box sx={{ color: c.dim, fontVariantNumeric: 'tabular-nums' }}>PE {fmtStrike(pe.strike)} ${fmtPx(priceOf(pe))} · CE {fmtStrike(ce.strike)} ${fmtPx(priceOf(ce))}</Box>
          <Box sx={{ flex: 1 }} />
          {ratio > 1.15 && btn('Match premiums', onMatch, 'amber', 'Move the cheaper short toward the money until its premium is closest to the dearer one (never past the other strike).')}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ color: c.faint, width: 92, flexShrink: 0 }}>Premium balance</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{ratioBar(ratio)}</Box>
        </Box>
        <Box sx={{ fontSize: 10.5, color: c.faint }}>
          {monthly
            ? <>Monthly expiry ({expLabel(ce.expiry)}). Playbook: open in the first ~5 days of the month; matched premiums keep the position near delta-neutral.</>
            : <>Weekly expiry — the playbook sells weeklies only after a 2–3 day move (see the 3-day chip in the top bar).</>}
        </Box>
      </Box>,
    );
  }

  // ── running: one block per held basket ──
  const ids = Array.from(new Set(legs.filter(l => l.held && l.heldBasketId != null).map(l => l.heldBasketId as number)));
  for (const id of ids) {
    const b = held[id];
    const mine = legs.filter(l => l.heldBasketId === id && l.enabled);
    const pair = shortPair(mine.map(l => ({ ...l, closing: false, price: priceOf(l) })));
    const booked = Number(b?.adjustmentsRealizedPnl || 0);
    const open = b?.live?.unrealizedPnl != null ? Number(b.live.unrealizedPnl) : null;
    const total = open != null ? booked + open : null;
    const fixed = b?.exitMode === 'FIXED' && b?.tpAmountInr > 0 && Number(b?.slAmountInr) !== 0 && Number.isFinite(Number(b?.slAmountInr));
    const adj = Array.isArray(b?.adjustmentLog) ? b.adjustmentLog.length : 0;
    const paper = b?.tradingMode === 'PAPER';
    blocks.push(
      <Box key={id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
          <Box sx={{ fontWeight: 700 }}>#{id} {b?.strategy || ''}</Box>
          {b && <Box component="span" sx={{ fontSize: 9.5, fontWeight: 800, px: 0.5, borderRadius: 0.5, bgcolor: paper ? c.amberBg : c.redBg, color: paper ? c.amber : c.red }}>{b.tradingMode}</Box>}
          <Box sx={{ flex: 1 }} />
          {pair && !pair.straddle && btn('Suggest roll', () => onSuggestRoll(id), pair.ratio >= ratioX ? 'red' : 'amber', 'Close the cheaper short (in profit) and re-sell the strike whose premium matches the dearer one.')}
          {b && btn('Exit rules', () => onExitRules(id), 'plain', 'Fixed ₹ target / stop on net total P&L (after slippage + fees), and the premium-ratio alert')}
          {btn('Close', () => onCloseBasket(id), 'red', paper ? 'Square off the whole paper basket at the live marks' : 'Close every leg with real market orders on Delta (asks to confirm)')}
        </Box>
        {pair ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box sx={{ color: c.faint, width: 92, flexShrink: 0 }}>Premium ratio</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>{ratioBar(pair.ratio)}</Box>
            </Box>
            <Box sx={{ color: c.dim, fontVariantNumeric: 'tabular-nums' }}>
              PE {fmtStrike(pair.pe.strike)} ${fmtPx(priceOf(pair.pe))} · CE {fmtStrike(pair.ce.strike)} ${fmtPx(priceOf(pair.ce))}
              {pair.straddle
                ? <Box component="span" sx={{ color: c.red, fontWeight: 700 }}> · Straddle reached — stop adjusting</Box>
                : <Box component="span" sx={{ color: c.faint }}> · {fmtStrike(pair.ce.strike - pair.pe.strike)} to straddle</Box>}
            </Box>
          </>
        ) : (
          <Box sx={{ color: c.faint, fontSize: 11 }}>Not a one-call / one-put short — the ratio rule doesn't apply.</Box>
        )}
        <Box sx={{ display: 'flex', gap: 1.4, flexWrap: 'wrap', color: c.dim, fontVariantNumeric: 'tabular-nums' }}>
          <span>Adjustments <b style={{ color: c.text }}>{adj}</b></span>
          <span>Booked <b style={{ color: booked >= 0 ? c.green : c.red }}>{inr(booked, true)}</b></span>
          <span>Open <b style={{ color: open == null ? c.faint : open >= 0 ? c.green : c.red }}>{open == null ? '—' : inr(open, true)}</b></span>
          <span>Total <b style={{ color: total == null ? c.faint : total >= 0 ? c.green : c.red }}>{total == null ? '—' : inr(total, true)}</b>{total != null && <span style={{ color: c.faint }}> net</span>}</span>
          {(() => {
            const m = margin?.[id];
            if (!m || m.marginInr == null) return null;
            const broker = m.source === 'BROKER';
            const rom = total != null && m.marginInr > 0 ? (total / m.marginInr) * 100 : null;
            return (
              <Tooltip title={broker
                ? `Margin Delta has blocked for these legs (its own per-position figure${m.estimateInr != null ? `; the app's estimate is ≈${inr(m.estimateInr)}` : ''}). Updated ${Math.max(0, Math.round((Date.now() - m.asOf) / 1000))}s ago.${rom != null ? ' Return on margin = net total P&L ÷ margin.' : ''}`
                : `Estimated margin for the open legs at today's marks — the same estimate the order preview uses${paper ? ' (a paper basket blocks no real margin)' : m.reason ? ` (Delta's own figure unavailable: ${m.reason})` : ''}.${rom != null ? ' Return on margin = net total P&L ÷ margin.' : ''}`}>
                <span data-testid="ws-held-margin" style={{ cursor: 'help' }}>
                  Margin <b style={{ color: c.text }}>{inr(m.marginInr)}</b> <span style={{ color: c.faint }}>{broker ? 'Delta' : 'est.'}</span>
                  {rom != null && <> · <b style={{ color: rom >= 0 ? c.green : c.red }}>{rom >= 0 ? '+' : ''}{rom.toFixed(1)}%</b> <span style={{ color: c.faint }}>on margin</span></>}
                </span>
              </Tooltip>
            );
          })()}
          {Number(b?.live?.costInr) > 0 && (
            <Tooltip title="Open and Total are NET: the P&L at the marks minus what closing would cost (the bid / ask) and the entry + exit fees. The target and stop are measured on this.">
              <span style={{ cursor: 'help' }}>costs <b style={{ color: c.text }}>≈{inr(Number(b.live.costInr), false)}</b></span>
            </Tooltip>
          )}
        </Box>
        {fixed && total != null ? (() => {
          // slAmountInr is signed: the stop sits at total P&L −sl (above ₹0 = a profit floor).
          const tp = Number(b.tpAmountInr), sl = Number(b.slAmountInr), stopAt = -sl;
          const span = tp - stopAt, pos = Math.max(0, Math.min(1, (total - stopAt) / span));
          const zero = Math.max(0, Math.min(1, -stopAt / span));
          return (
            <Tooltip title="Net total P&L (booked + open, after slippage + fees) between the fixed stop (left) and target (right). Nothing moves these on its own — drag them on the basket chart.">
              <Box>
                <Box sx={{ position: 'relative', height: 8, borderRadius: 4, background: `linear-gradient(90deg, ${stopAt >= 0 ? c.greenBg : c.redBg}, ${c.hover} ${zero * 100}%, ${c.greenBg})` }}>
                  {stopAt < 0 && <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${zero * 100}%`, width: '1px', bgcolor: c.faint }} />}
                  <Box sx={{ position: 'absolute', top: -3, width: 4, height: 14, borderRadius: 1, left: `calc(${pos * 100}% - 2px)`, bgcolor: total >= 0 ? c.green : c.red }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: c.faint, mt: 0.3 }}>
                  <span>SL {stopAt >= 0 ? '+' : '−'}{inr(Math.abs(stopAt))}{stopAt > 0 ? ' (profit lock)' : ''}</span><span>TP +{inr(tp)}</span>
                </Box>
              </Box>
            </Tooltip>
          );
        })() : b && (
          <Box sx={{ fontSize: 10.5, color: c.faint }}>Exits: premium levels (re-based on each adjustment). Set <b>Exit rules → Fixed ₹</b> to follow the playbook.</Box>
        )}
      </Box>,
    );
  }

  if (!blocks.length) return null;
  return (
    <Box sx={{ mx: 0.8, mt: 0.8, p: 0.9, borderRadius: 1, border: `1px solid ${c.border}`, bgcolor: c.isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)', fontSize: 11.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ fontSize: 10.5, letterSpacing: 0.8, fontWeight: 700, color: c.dim }}>STRANGLE PLAYBOOK</Box>
      {blocks}
    </Box>
  );
};

export default React.memo(PlaybookCard);
